import * as _ from 'lodash-es';
import { bubbleFormats } from 'quill/blots/block';
import type { AttributeMap } from 'quill/core';
import type Quill from 'quill/core';
import { Parchment } from 'quill/core';
import List from 'quill/formats/list';
import type { WordResult } from '../../workers/spellCorrector/spellCorrector';
import getClosestLine from '../utils/getClosestLine';

const END_PUNCTUATIONS = new Set(['.', '?', '!']);

const getLastNonBlankChar = (text: string) => _.last(text.trimEnd());

const checkIsSentenceStart = (
  textBeforeWord: string,
  quill: Quill,
  leafIndex: number,
  leaf: Parchment.LeafBlot,
) => {
  const lastNonBlankChar = getLastNonBlankChar(textBeforeWord);
  if (lastNonBlankChar) {
    return END_PUNCTUATIONS.has(lastNonBlankChar);
  }

  const [line, offset] = quill.getLine(leafIndex);
  if (line && offset === 0) {
    return true;
  }

  const prevLeaf = quill.getLeaf(leafIndex)[0];
  if (prevLeaf !== leaf && prevLeaf instanceof Parchment.TextBlot) {
    const prevLeafLastNonBlankChar = getLastNonBlankChar(prevLeaf.value());
    if (prevLeafLastNonBlankChar) {
      return END_PUNCTUATIONS.has(prevLeafLastNonBlankChar);
    }
  }

  return false;
};

const getAllowedWordCases = (result: WordResult, isSentenceStart: boolean) => {
  const { metadata, word } = result;
  const cases: string[] = [];
  if (metadata.preferredCase) {
    cases.push(metadata.preferredCase);
  }

  if (metadata.isAllLettersLowerCasedAllowed && !isSentenceStart) {
    cases.push(word);
  }

  if (
    // We want the spelling correction feature to be conservative.
    // If `isAllLettersLowerCasedAllowed` is true, we allow words to have their first letters capitalized.
    // This is because we can't determine whether a word is a proper noun or not.
    // It's unrealistic to include all proper nouns in the dictionary, as new ones,
    // such as brands, are created every day.
    metadata.isAllLettersLowerCasedAllowed ||
    metadata.isInitialCapitalizedAllowed
  ) {
    cases.push(_.capitalize(word));
  }

  return cases;
};

export interface SpellCorrectionResult {
  original: string;
  corrected: string;
  formats: AttributeMap;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export type LookupWordFunction = (
  query: string,
  onlyExactMatch: boolean,
) => Promise<WordResult | null>;

const correctPrecedingWord = async (
  quill: Quill,
  index: number,
  lookupWord: LookupWordFunction,
): Promise<SpellCorrectionResult | null> => {
  const [leaf, offset] = quill.getLeaf(index);
  if (!(leaf instanceof Parchment.TextBlot)) return null;
  const line = getClosestLine(leaf);
  if (line instanceof List) return null;

  const text = leaf.value().slice(0, offset);
  const spaceIndex = text.lastIndexOf(' ');
  const word = spaceIndex === -1 ? text : text.substring(spaceIndex + 1);

  const maybeWord = word.match(/^[a-z][a-z\-']*$/i);
  if (!maybeWord) return null;

  // don't correct words in full caps, as they may be acronyms, abbreviations or used for emphasis.
  const isInFullCaps = word.toUpperCase() === word;
  if (isInFullCaps) return null;

  const formats = bubbleFormats(leaf);
  if (formats.code) return null;

  const getCorrectionResult = (
    original: string,
    wordResult: WordResult | null,
  ): SpellCorrectionResult | null => {
    if (!wordResult) return null;

    const isSentenceStart = checkIsSentenceStart(
      text.substring(0, spaceIndex),
      quill,
      index - offset,
      leaf,
    );

    const allowed = getAllowedWordCases(wordResult, isSentenceStart);
    const firstAllowedCase = allowed[0];
    if (firstAllowedCase && !allowed.includes(original)) {
      return { original, corrected: firstAllowedCase, formats };
    }

    return null;
  };

  const isExactMatchRequired = !!word.match(/[A-Z]/);

  const possessiveMatch = word.match(/^(.+)('s?)$/);
  if (possessiveMatch) {
    const result = await lookupWord(word, true);
    if (result) return getCorrectionResult(word, result);

    const [, baseWord, suffix] = possessiveMatch as [string, string, string];
    const resultForBaseWord = await lookupWord(baseWord, isExactMatchRequired);
    if (resultForBaseWord) {
      const correctionResult = getCorrectionResult(baseWord, resultForBaseWord);
      return (
        correctionResult && {
          ...correctionResult,
          original: correctionResult.original + suffix,
          corrected: correctionResult.corrected + suffix,
        }
      );
    }
  }

  return getCorrectionResult(
    word,
    await lookupWord(word, isExactMatchRequired),
  );
};

export default correctPrecedingWord;
